import React, {
  forwardRef, memo, useCallback, useEffect, useId, useRef, useState,
} from "react";
import { X, Loader2, Search as SearchIcon } from "lucide-react";

/**
 * ChainFlow OS · PremiumInput
 * ────────────────────────────────────────────────────────────
 * The fifth brand-level primitive — and the one that closes the
 * form layer. Where the four value-pickers handle discrete numeric,
 * continuous numeric, low-cardinality categorical, and high-
 * cardinality categorical, `PremiumInput` handles **free-form
 * value entry**: text, email, phone, currency, prices, dates,
 * addresses, notes, search.
 *
 * Locked v1 interaction grammar:
 *  • Native HTML `<input>` underneath so platform behaviours (IME
 *    composition, autofill, password managers, native date pickers,
 *    `inputMode` numeric keyboards on mobile) all work first time.
 *  • Mouse + touch parity is inherent to native inputs; we layer the
 *    brand focus / hover / disabled grammar on top.
 *  • Optional `clearable` X surfaces only when the field has a value
 *    and is not disabled / readonly.
 *  • Optional `loading` swaps the right-edge affordance to a calm
 *    spinner; the input itself remains read-only while loading.
 *  • Helper / error / required hint render below; error sets
 *    `aria-invalid` + rose border + rose ring + role="alert" hint.
 *  • Disabled + readonly are first-class and visually distinct.
 *
 * Locked v1 visual identity (matches PremiumSelect):
 *  • Soft white surface, slate-200 border, slate-300 on hover.
 *  • Violet focus-visible ring (rose for error). 180 ms transitions.
 *  • Rounded-xl. Sizes `sm` (h-8) / `md` (h-10) / `lg` (h-11).
 *  • Optional prefix / suffix (text or React node) renders inside
 *    the field with calm slate-500 tone and a subtle divider only
 *    when both prefix/suffix and a clear/loading affordance coexist.
 *  • `tabular-nums` baseline on numeric / currency variants so
 *    digits don't shimmer.
 *  • `prefers-reduced-motion` suppresses all transitions.
 *
 * Convenience formats (`format` prop):
 *  • "currency"  → prefix="£" by default, inputMode="decimal".
 *  • "percent"   → suffix="%",          inputMode="decimal".
 *  • "search"    → prefix is a Search icon (consumer can override).
 *
 * @prop {string|number} value
 * @prop {(v) => void} onChange   Called with the raw string value.
 * @prop {string} [type="text"]   Any native input type.
 * @prop {"currency"|"percent"|"search"|null} [format]
 * @prop {string} [label]
 * @prop {string} [hint]
 * @prop {string} [error]
 * @prop {string} [placeholder]
 * @prop {React.Node|string} [prefix]
 * @prop {React.Node|string} [suffix]
 * @prop {boolean} [clearable=false]
 * @prop {boolean} [loading=false]
 * @prop {boolean} [readOnly=false]
 * @prop {boolean} [disabled=false]
 * @prop {boolean} [required=false]
 * @prop {"sm"|"md"|"lg"} [size="md"]
 * @prop {string} [autoComplete]
 * @prop {string} [inputMode]
 * @prop {"light"|"dark"} [tone="light"]
 *   v1.1 · Adapts borders / background / text for dark operational
 *   surfaces (slate-900 / navy panels). Identical interaction grammar
 *   and focus ring family — only tonal colour shifts.
 * @prop {"framed"|"bare"} [variant="framed"]
 *   v1.1 · `bare` removes the outer rounded surface (border, bg,
 *   ring, padding). Use for palette-style spotlight inputs
 *   (GlobalSearch / CommandPalette) where the host card already
 *   provides the chrome. Keyboard / accessibility / clearable /
 *   loading / error grammar identical.
 * @prop {string} [name]
 * @prop {number} [maxLength]
 * @prop {string} [testid]
 *   Yields `testid-wrap`, `testid`, `testid-clear`, `testid-error`.
 * @prop {string} [className]
 * @prop {(e) => void} [onBlur]
 * @prop {(e) => void} [onFocus]
 * @prop {(e) => void} [onKeyDown]
 */
const PremiumInputInner = forwardRef(function PremiumInputInner({
  value,
  onChange,
  type = "text",
  format = null,
  label,
  hint,
  error,
  placeholder,
  prefix,
  suffix,
  clearable = false,
  loading = false,
  readOnly = false,
  disabled = false,
  required = false,
  size = "md",
  tone = "light",
  variant = "framed",
  autoComplete,
  inputMode,
  name,
  maxLength,
  testid,
  className = "",
  onBlur,
  onFocus,
  onKeyDown,
  ...rest
}, externalRef) {
  const innerRef = useRef(null);
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const [focused, setFocused] = useState(false);

  // Wire external ref to the native input.
  useEffect(() => {
    if (typeof externalRef === "function") externalRef(innerRef.current);
    else if (externalRef) externalRef.current = innerRef.current;
  }, [externalRef]);

  // Format shorthands — derive sensible defaults without overriding consumer.
  const resolvedPrefix = prefix !== undefined ? prefix
    : format === "currency" ? "£"
    : format === "search"   ? <SearchIcon size={14} strokeWidth={2.3} />
    : undefined;
  const resolvedSuffix = suffix !== undefined ? suffix
    : format === "percent" ? "%"
    : undefined;
  const resolvedInputMode = inputMode
    || (format === "currency" || format === "percent" ? "decimal" : undefined);
  const resolvedType = type === "text" && format === "currency" ? "text" : type;

  const hasError = !!error;
  const hasValue = value !== undefined && value !== null && value !== "";
  const showClear = clearable && hasValue && !disabled && !readOnly && !loading;
  const showSpinner = loading;

  /* P · Create Deal Room · Step 2 Final Polish (2026-02-28)
   * ---------------------------------------------------------
   * Currency dedup — when `format="currency"` we already render
   * the `£` as an aria-hidden prefix affix. If the caller (a
   * preset from a property row, a re-open of the wizard, an
   * inbound handoff from Contacts) supplies a value that
   * already carries a `£` sign, the field previously rendered
   * "£ £745,000".  We strip any leading currency symbols +
   * whitespace here so the visible input only shows the
   * numeric portion — the raw `value` in the consumer's state
   * is untouched. */
  const displayedValue = (format === "currency" && typeof value === "string")
    ? value.replace(/^\s*£\s*/, "")
    : (value ?? "");

  const sizing = {
    sm: { wrap: "h-8 px-2.5 text-[12.5px]", input: "" },
    md: { wrap: "h-10 px-3 text-[13.5px]",  input: "" },
    lg: { wrap: "h-11 px-3.5 text-[14px]",  input: "" },
  }[size] || { wrap: "h-10 px-3 text-[13.5px]", input: "" };

  const handleChange = useCallback((e) => onChange?.(e.target.value), [onChange]);

  const handleFocus = useCallback((e) => {
    setFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e) => {
    setFocused(false);
    onBlur?.(e);
  }, [onBlur]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    onChange?.("");
    innerRef.current?.focus();
  }, [onChange]);

  // Numeric format: prevent non-numeric chars on currency/percent.
  // Allow digits, decimal point, leading minus (for negative if needed),
  // and the standard editing keys.
  const handleKeyDown = useCallback((e) => {
    if (format === "currency" || format === "percent") {
      const allowEditing = [
        "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End",
        "Tab", "Enter", "Escape",
      ].includes(e.key);
      const isModifier = e.ctrlKey || e.metaKey || e.altKey;
      const isNumeric = /^[0-9.,\-]$/.test(e.key);
      if (!allowEditing && !isModifier && e.key.length === 1 && !isNumeric) {
        e.preventDefault();
      }
    }
    onKeyDown?.(e);
  }, [format, onKeyDown]);

  const isNumericFormat = format === "currency" || format === "percent" || type === "number";
  const isDark = tone === "dark";
  const isBare = variant === "bare";

  // Tonal palettes — keep this in one place so the contract is auditable.
  const labelTone = isDark ? "text-slate-300" : "text-slate-500";
  const placeholderTone = isDark ? "placeholder-slate-500" : "placeholder-slate-400";
  const textTone = isDark ? "text-slate-100" : "text-slate-900";
  const affixTone = isDark ? "text-slate-400" : "text-slate-500";
  const clearTone = isDark
    ? "text-slate-500 hover:bg-slate-700/40 hover:text-slate-200"
    : "text-slate-400 hover:bg-slate-100 hover:text-slate-700";
  const spinnerTone = isDark ? "text-slate-500" : "text-slate-400";

  return (
    <div className={`cf-inp ${className}`} data-tone={tone} data-testid={testid ? `${testid}-wrap` : undefined}>
      {label && (
        <label
          htmlFor={inputId}
          className={`block text-[10.5px] font-semibold uppercase tracking-[0.14em] mb-1.5 ${labelTone}`}
        >
          {label}
          {required && <span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>}
        </label>
      )}
      <div
        className={
          `cf-inp-wrap relative flex items-center gap-2 ${isBare ? "" : sizing.wrap} ` +
          (isBare
            ? ""
            : "rounded-xl border " +
              (isDark
                ? (hasError
                    ? "border-rose-400/50 bg-slate-900/40"
                    : "border-slate-700/60 bg-slate-900/30 hover:border-slate-600/80")
                : (hasError
                    ? "border-rose-300 bg-white"
                    : "border-slate-200 bg-white hover:border-slate-300"))
          ) +
          (!isBare && focused
            ? hasError
              ? " ring-2 ring-rose-300/60 border-rose-300"
              : (isDark
                  ? " ring-2 ring-violet-400/40 border-slate-500/80"
                  : " ring-2 ring-violet-400/40 border-slate-300")
            : "") +
          (!isBare && disabled
            ? (isDark ? " opacity-60 cursor-not-allowed bg-slate-900/50" : " opacity-60 cursor-not-allowed bg-slate-50")
            : "") +
          (!isBare && readOnly && !disabled
            ? (isDark ? " bg-slate-900/40" : " bg-slate-50")
            : "")
        }
      >
        {resolvedPrefix !== undefined && (
          <span
            className={`cf-inp-affix ${affixTone} flex-none inline-flex items-center pointer-events-none`}
            aria-hidden="true"
          >
            {resolvedPrefix}
          </span>
        )}
        <input
          ref={innerRef}
          id={inputId}
          name={name}
          type={resolvedType}
          inputMode={resolvedInputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          required={required}
          disabled={disabled}
          readOnly={readOnly || loading}
          placeholder={placeholder}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : (hint ? hintId : undefined)}
          aria-required={required || undefined}
          value={displayedValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          data-testid={testid}
          className={
            "flex-1 min-w-0 bg-transparent outline-none border-none " +
            `${placeholderTone} ${textTone} ` +
            (isNumericFormat ? "tabular-nums " : "") +
            (disabled ? "cursor-not-allowed " : "") +
            sizing.input
          }
          {...rest}
        />
        {resolvedSuffix !== undefined && !showClear && !showSpinner && (
          <span
            className={`cf-inp-affix ${affixTone} flex-none inline-flex items-center`}
          >
            {resolvedSuffix}
          </span>
        )}
        {showClear && (
          <button
            type="button"
            onClick={handleClear}
            tabIndex={-1}
            aria-label="Clear"
            data-testid={testid ? `${testid}-clear` : undefined}
            className={`w-5 h-5 rounded-md inline-flex items-center justify-center flex-none ${clearTone}`}
          >
            <X size={13} strokeWidth={2.4} />
          </button>
        )}
        {showSpinner && (
          <Loader2
            size={14}
            strokeWidth={2.4}
            className={`cf-inp-spinner ${spinnerTone} flex-none`}
            aria-hidden="true"
          />
        )}
      </div>
      {(hint && !error) && (
        <p id={hintId} className="mt-1.5 text-[11px] text-slate-500 leading-snug">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          data-testid={testid ? `${testid}-error` : undefined}
          className="mt-1.5 text-[11px] text-rose-600 leading-snug"
        >
          {error}
        </p>
      )}
      <style>{`
        .cf-inp-wrap { transition: border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease; }
        .cf-inp-spinner { animation: cf-inp-spin 900ms linear infinite; }
        @keyframes cf-inp-spin {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-inp-wrap { transition: none !important; }
          .cf-inp-spinner { animation: none !important; }
        }
        /* iter-alignment · Autofill polish (2026)
         * ---------------------------------------------------
         * Chrome/Safari paint a bright yellow #FAFFBD wash on
         * autofilled inputs which fights the brand palette
         * (especially on login).  There is no property that
         * suppresses this directly; the platform trick is a
         * long inset box-shadow that recolours the entire
         * autofill background + a text-fill-color override
         * for the value + caret.  Password managers keep
         * working — we don't touch autocomplete or the
         * hidden overlay.  Delay eases the color-transition
         * flash on refill.
         *
         * Light tone → subtle lavender (#F5F3FF), navy text
         *              (#0F172A / slate-900), navy caret.
         * Dark tone  → slate-900 wash, slate-100 text.
         */
        .cf-inp input:-webkit-autofill,
        .cf-inp input:-webkit-autofill:hover,
        .cf-inp input:-webkit-autofill:focus,
        .cf-inp input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #F5F3FF inset !important;
                  box-shadow: 0 0 0 1000px #F5F3FF inset !important;
          -webkit-text-fill-color: #0F172A !important;
                        caret-color: #0F172A !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        .cf-inp[data-tone="dark"] input:-webkit-autofill,
        .cf-inp[data-tone="dark"] input:-webkit-autofill:hover,
        .cf-inp[data-tone="dark"] input:-webkit-autofill:focus,
        .cf-inp[data-tone="dark"] input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #0F172A inset !important;
                  box-shadow: 0 0 0 1000px #0F172A inset !important;
          -webkit-text-fill-color: #F1F5F9 !important;
                        caret-color: #F1F5F9 !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
});

PremiumInputInner.displayName = "PremiumInputInner";

export const PremiumInput = memo(PremiumInputInner);
PremiumInput.displayName = "PremiumInput";

export default PremiumInput;
